<template>
  <div id="scroll">
    <AccountNavbar />
    <div class="tip" v-if="showTip">
      <img class="tip-icon" src="@/assets/integration.png" alt="Integration">
      <p class="tip-message">Want to integrate with Practice Fusion? <a
          href="https://calendly.com/dashquill/15-minute-meeting" target="_blank">Schedule a demo with a DashQuill team
          member.</a></p>
      <img class="close-tip" src="@/assets/close.png" alt="Close Tip" @click="hideTipForever()">
    </div>
    <div class="content">
      <div class="content-header">
        <h1>Patient Submissions</h1>
        <div class="button-list">
          <button class="button primary small" @click="openModal('intake')">
            Intake Patient
          </button>
        </div>
      </div>
      <div class="submission-header">
        <PageNumList :isLoading="!loaded" @startLoading="loaded = false" @doneLoading="loaded = true" />
        <div class="filter-container">
          <img src="@/assets/filter.png" alt="Filter" class="filter-icon"
            :data-active="store.filterSelectedForms.size + store.filterSelectedStatuses.size > 0"
            @click.prevent.stop="isFilterMenuOpen = !isFilterMenuOpen">
          <transition name="menu" mode="out-in">
            <filter-menu ref="menu" v-if="isFilterMenuOpen" @onFilterChanged="refreshSubmissions" />
          </transition>
        </div>
        <SearchSubmissions />
        <select class="form-control page-size" v-model="store.submissionPageSize" @change="refreshSubmissions">
          <option :value="10">10 items</option>
          <option :value="50">50 items</option>
          <option :value="100">100 items</option>
        </select>
      </div>
      <transition name="appear" mode="out-in">
        <div class="content-body" v-if="loaded" :key="store.submissions">
          <SubmissionListItem v-for="submission of store.submissions" :key="submission._id" :submission="submission"
            @click="openModal('details', submission)" @deleteSubmission="openModal('delete', submission)" />
          <p v-if="store.submissions.length == 0" class="dq-edge-case">
            No submissions found. Intake a patient to get started.
          </p>
        </div>
      </transition>
    </div>
    <transition name="modal" mode="out-in">
      <IntakePatientModal v-if="modal.id == 'intake'" @closeModal="closeModal" />
      <SubmissionDetailsModal v-else-if="modal.id == 'details'" :submission="modal.data" @closeModal="closeModal" />
      <DeleteSubmissionModal v-else-if="modal.id == 'delete'" :submission="modal.data" @closeModal="closeModal" />
      <WelcomeModal v-else-if="modal.id == 'welcome'" @closeModal="closeModal" />
    </transition>
  </div>
</template>

<script>
import AccountNavbar from "../components/AccountNavbar.vue";
import SubmissionListItem from "../components/account-submissions/SubmissionListItem.vue";
import IntakePatientModal from "../modals/account-submissions/IntakePatientModal.vue";
import SubmissionDetailsModal from "../modals/account-submissions/SubmissionDetailsModal.vue";
import DeleteSubmissionModal from "../modals/account-submissions/DeleteSubmissionModal.vue";
import WelcomeModal from "@/modals/account-submissions/WelcomeModal.vue";
import PageNumList from "@/components/account-submissions/PageNumList.vue";
import SearchSubmissions from "../components/account-submissions/SearchSubmissions.vue";
import { loadSubmissionsForCurrentPage, fetchAccountInfo, fetchSubmissionById, store } from "@/api";
import FilterMenu from '../components/account-submissions/FilterMenu.vue';

export default {
  name: "AccountSubmissionsView",
  data() {
    return {
      store,
      modal: {
        id: "",
        open: false,
        data: null,
      },
      loaded: false,
      showTip: false,
      isFilterMenuOpen: false
    };
  },
  mounted() {
    window.addEventListener('click', this.handleMouseClick);
  },
  unmounted() {
    window.removeEventListener('click', this.handleMouseClick);
  },
  async created() {
    const query = this.$route.query;
    if (query.welcome != null) {
      this.openModal('welcome');
    }

    // Show tip if it's not hidden
    const showTipStatus = localStorage.getItem('dashquill-show-tip-status');
    this.showTip = showTipStatus != 'hidden';

    // Check if there's an id query parameter to open a specific submission
    if (query.id) {
      this.loadSubmissionById(query.id);
    }

    try {
      this.loaded = false;
      await this.checkLoginStatus();
      await loadSubmissionsForCurrentPage();
    } catch (err) {
      console.error("Failed to fetch account forms or presets", err);
    } finally {
      this.loaded = true;
    }
  },
  methods: {
    async checkLoginStatus() {
      try {
        await fetchAccountInfo();
      } catch (err) {
        if (err.message === 'Permission denied') {
          this.$router.replace('/login');
        }
      }
    },
    async loadSubmissionById(id) {
      try {
        const submission = await fetchSubmissionById(id);
        this.openModal('details', submission);
      } catch (err) {
        console.error("Failed to fetch submission by id", err);
        alert('Failed to load submission: ' + err.message);
      }
    },
    openModal(id, data) {
      this.modal = {
        open: true,
        id: id,
        data: data,
      };
    },
    closeModal() {
      this.modal = {
        open: false,
        id: "",
        data: null,
      };
    },
    hideTipForever() {
      localStorage.setItem('dashquill-show-tip-status', 'hidden');
      this.showTip = false;
    },
    handleMouseClick(event) {
      const menu = this.$refs.menu;
      if (!menu) return;

      const target = event.target;
      if (this.isFilterMenuOpen && !menu.$el.contains(target)) {
        this.isFilterMenuOpen = false;
        event.preventDefault();
        event.stopPropagation();
      }

    },
    async refreshSubmissions() {
      try {
        this.store.submissionPage = 1;
        this.loaded = false;
        await loadSubmissionsForCurrentPage();
      } catch (err) {
        console.error("Failed to fetch account forms or presets", err);
        alert('Error: ' + err.message);
      } finally {
        this.loaded = true;
      }
    }
  },
  components: {
    AccountNavbar,
    SubmissionListItem,
    IntakePatientModal,
    SubmissionDetailsModal,
    DeleteSubmissionModal,
    PageNumList,
    SearchSubmissions,
    WelcomeModal,
    FilterMenu
  },
};
</script>

<style scoped>
.content {
  width: 100%;
  max-width: 1000px;
  min-height: 700px;
  background: #fff;
  padding: 50px;
  box-shadow: 0px 0px 8px #00000011;
  border-radius: 10px;
  margin: auto;
}

.content-header {
  margin-bottom: 30px;
  display: flex;
  align-items: center;
}

.button-list {
  display: flex;
  flex: 1;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
}

.button-list .button {
  margin-left: 20px;
}

.submission-header {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  gap: 10px;
}

.tip {
  max-width: 1000px;
  margin: auto;
  padding: 15px;
  background: #fff;
  color: #000;
  border-radius: 10px;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  box-shadow: 0px 0px 8px #00000022;
}

.tip-icon {
  height: 30px;
  margin-right: 12px;
}

.tip-message {
  flex: 1;
}

.close-tip {
  height: 16px;
  cursor: pointer;
}

.dq-edge-case {
  color: rgb(153, 153, 153);
  font-style: italic;
}

.page-size {
  width: 120px;
}

.filter-container {
  position: relative;
}

.filter-icon {
  width: 30px;
  height: 30px;
  display: block;
  opacity: 0.3;
  padding: 8px;
}

.filter-icon:hover {
  opacity: 1;
  cursor: pointer;
}

.filter-icon[data-active="true"] {
  opacity: 1;
  background: #EEE;
  border-radius: 10px;
}

.menu-enter-active,
.menu-leave-active {
  transition: all ease-in-out 0.2s;
  transform-origin: top right;
}

.menu-enter-from,
.menu-leave-to {
  opacity: 0;
  transform: scale(0.95);
}
</style>